import {
  clearBannerDecision,
  createBannerSource,
  reopenBanner,
  resolveBannerNoticeLanguage,
} from "@/core/consent/banner";
import type { ConsentEngine } from "@/core/consent/engine/consent.engine";
import { createConsentEngine } from "@/core/consent/engine/consent.engine";
import {
  registerConsentBannerReopener,
  setActiveConsentStore,
} from "@/core/consent/engine/consent.runtime";
import { getCurrentNoticeVersion } from "@/core/consent/engine/consentNotice";
import {
  CONSENT_RECORD_FINGERPRINT_STORAGE_KEY,
  CONSENT_RECORD_REJECTED_STORAGE_KEY,
  createConsentDecisionRecorder,
} from "@/core/consent/recording/decision.recorder";
import { createGpcSource } from "@/core/consent/sources/gpc.source";
import {
  CONSENT_DECISION_STORAGE_KEY,
  createStorageSource,
} from "@/core/consent/sources/storage.source";

export type ConsentStackOptions = {
  /**
   * The route's locale. The language recorded with each decision is what the
   * mounted banner PRESENTED for that locale (`banner.ts`), which is not
   * necessarily the locale itself. A getter is accepted so a language switch
   * mid-session is reported without restarting the engine.
   */
  readonly routeLanguageCode: string | (() => string);
};

export type ConsentStack = {
  /** The engine's read side, for whoever needs to observe consent (the instrumentation bus does). */
  readonly engine: ConsentEngine;
  /** Start the sources. Subscribe every observer BEFORE this: the stored decision replays synchronously. */
  readonly start: () => void;
  readonly stop: () => void;
};

/**
 * Consent, assembled by consent: the engine over its sources (a stored
 * decision, the browser's GPC signal, the mounted banner), the first-party
 * recorder observing it, the banner's reopen registered for `useConsent`, and
 * the live store published for React. The app composes THIS, never the parts —
 * which is what keeps every CMP name behind `banner.ts`.
 *
 * Construction wires the observers in the order they must learn of a
 * resolution — React first (so the page reflects the decision), the recorder
 * next (evidence before vendors) — and leaves `start` to the caller so a
 * further observer (the instrumentation bus) can subscribe before the storage
 * source replays a saved decision. `stop` releases everything; construct →
 * stop → construct is what React StrictMode does, and it is safe.
 */
export const createConsentStack = ({ routeLanguageCode }: ConsentStackOptions): ConsentStack => {
  const engine = createConsentEngine({
    sources: [createStorageSource(), createGpcSource(), createBannerSource()],
    noticeVersion: getCurrentNoticeVersion(),
  });
  const recorder = createConsentDecisionRecorder({
    consentLanguageCode: () =>
      resolveBannerNoticeLanguage(
        typeof routeLanguageCode === "function" ? routeLanguageCode() : routeLanguageCode,
      ),
  });

  setActiveConsentStore(engine);
  const unsubscribeRecorder = engine.subscribe(recorder);
  const unregisterReopener = registerConsentBannerReopener(reopenBanner);

  return {
    engine,
    start: () => {
      engine.start();
    },
    stop: () => {
      unregisterReopener();
      unsubscribeRecorder();
      setActiveConsentStore(null);
      engine.stop();
    },
  };
};

/** Where this browser keeps consent state, for diagnostics (the snap debug panel). */
export const CONSENT_STORAGE_KEYS = {
  decision: CONSENT_DECISION_STORAGE_KEY,
  recorded: CONSENT_RECORD_FINGERPRINT_STORAGE_KEY,
  rejected: CONSENT_RECORD_REJECTED_STORAGE_KEY,
} as const;

/**
 * Forget every consent decision this browser holds — the engine's persisted
 * answer, the recorder's markers, and the banner's own memory — so the next
 * load asks again. Diagnostics only.
 */
export const clearStoredConsent = (): void => {
  try {
    for (const key of Object.values(CONSENT_STORAGE_KEYS)) {
      window.localStorage.removeItem(key);
    }
  } catch {
    // Storage unavailable — nothing to clear.
  }
  clearBannerDecision();
};
